import React from 'react';
import { useTranslation } from 'react-i18next';
import { Megaphone, TrendingUp, Users, Calendar, Mail, Target, Clock, DollarSign, BarChart } from 'lucide-react';
import { Meta } from '../components/Meta';

export function Advertise() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen">
      <Meta 
        title={t('advertise.metaTitle')}
        description={t('advertise.metaDesc')}
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-[#F3F7EE] to-white py-24">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl lg:text-5xl font-gelica leading-tight mb-6">
                {t('advertise.title')}
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                {t('advertise.subtitle')}
              </p>
              <a 
                href="mailto:support@mindful.family"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-[#8DA847] hover:bg-[#7A9339] transition-colors"
              >
                <Mail className="h-5 w-5 mr-2" />
                {t('advertise.contactTeam')}
              </a>
            </div>
            <div className="mt-12 lg:mt-0">
              <img 
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&q=80&w=2000"
                alt="Business Growth"
                className="rounded-xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Grid */}
      <div className="py-24 bg-white">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-gelica font-bold text-content mb-4">
              {t('advertise.whyAdvertise')}
            </h2>
            <p className="text-lg text-content/70 max-w-2xl mx-auto">
              {t('advertise.whyAdvertiseDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#F3F7EE] rounded-xl p-6 hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-[#8DA847] rounded-lg flex items-center justify-center mb-4">
                <Target className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-content mb-2">{t('advertise.targetedReach')}</h3>
              <p className="text-content/70">
                {t('advertise.targetedReachDesc')}
              </p>
            </div>

            <div className="bg-[#F3F7EE] rounded-xl p-6 hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-[#8DA847] rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-content mb-2">{t('advertise.increasedVisibility')}</h3>
              <p className="text-content/70">
                {t('advertise.increasedVisibilityDesc')}
              </p>
            </div>

            <div className="bg-[#F3F7EE] rounded-xl p-6 hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-[#8DA847] rounded-lg flex items-center justify-center mb-4">
                <BarChart className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-content mb-2">{t('advertise.performanceTracking')}</h3>
              <p className="text-content/70">
                {t('advertise.performanceTrackingDesc')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Advertising Options */}
      <div className="py-24 bg-[#F3F7EE]">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-gelica font-bold text-content mb-4">
              {t('advertise.advertisingOptions')}
            </h2>
            <p className="text-lg text-content/70 max-w-2xl mx-auto">
              {t('advertise.advertisingOptionsDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center mb-4">
                <Users className="h-6 w-6 text-[#8DA847] mr-3" />
                <h3 className="text-lg font-semibold text-content">{t('advertise.practitionerSpotlight')}</h3>
              </div>
              <p className="text-content/70 mb-4">
                {t('advertise.practitionerSpotlightDesc')}
              </p>
              <div className="flex items-center text-sm text-content/60">
                <Clock className="h-4 w-4 mr-2" />
                <span>{t('advertise.duration1to3Months')}</span>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center mb-4">
                <Calendar className="h-6 w-6 text-[#8DA847] mr-3" />
                <h3 className="text-lg font-semibold text-content">{t('advertise.eventPromotion')}</h3>
              </div>
              <p className="text-content/70 mb-4">
                {t('advertise.eventPromotionDesc')}
              </p>
              <div className="flex items-center text-sm text-content/60">
                <Clock className="h-4 w-4 mr-2" />
                <span>{t('advertise.eventDurationBased')}</span>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center mb-4">
                <Megaphone className="h-6 w-6 text-[#8DA847] mr-3" />
                <h3 className="text-lg font-semibold text-content">{t('advertise.newsFeedAds')}</h3>
              </div>
              <p className="text-content/70 mb-4">
                {t('advertise.newsFeedAdsDesc')}
              </p>
              <div className="flex items-center text-sm text-content/60">
                <Clock className="h-4 w-4 mr-2" />
                <span>{t('advertise.weeklyOrMonthly')}</span>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center mb-4">
                <DollarSign className="h-6 w-6 text-[#8DA847] mr-3" />
                <h3 className="text-lg font-semibold text-content">{t('advertise.customPackages')}</h3>
              </div>
              <p className="text-content/70 mb-4">
                {t('advertise.customPackagesDesc')}
              </p>
              <div className="flex items-center text-sm text-content/60">
                <Clock className="h-4 w-4 mr-2" />
                <span>{t('advertise.flexibleDuration')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <h2 className="text-3xl font-gelica font-bold text-content mb-6">
            {t('advertise.readyToGrow')}
          </h2>
          <p className="text-xl text-content/70 mb-8">
            {t('advertise.readyToGrowDesc')}
          </p>
          <a 
            href="mailto:support@mindful.family"
            className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-white bg-[#8DA847] hover:bg-[#7A9339] transition-colors"
          >
            <Mail className="h-5 w-5 mr-2" />
            {t('advertise.getInTouch')}
          </a>
          <p className="mt-4 text-sm text-content/60">
            {t('advertise.respondWithin24h')}
          </p>
        </div>
      </div>
    </div>
  );
}